import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { Avatar, VerifiedMark, DemoBadge } from '../lib/ui';
import { Icon } from '../lib/icons';
import Screen from '../components/Screen';
import MemberLayout from '../components/MemberLayout';
import api from '../lib/api';
import { notifyError } from '../lib/toast';

// ── Inbox: only the circles the user is a member of ──────────────────────
function ChatInbox() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    let on = true;
    api.get('/me/chats').then(r => on && setItems(r.data)).catch(() => on && setItems([]));
    return () => { on = false; };
  }, []);

  return (
    <MemberLayout>
      <div style={{ padding: '16px 20px 12px', flexShrink: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>แชท</div>
        <div style={{ fontSize: 12.5, color: PS.textMuted, marginTop: 2 }}>เฉพาะวงแชร์ที่คุณร่วมเล่นอยู่</div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', paddingBottom: 12 }}>
        {items === null && (
          <div className="ps-live-dot" style={{ padding: 40, textAlign: 'center', color: PS.textMuted, fontSize: 13 }}>กำลังโหลด…</div>
        )}
        {items && items.length === 0 && (
          <div style={{ margin: '12px 16px', padding: 24, textAlign: 'center', color: PS.textMuted, fontSize: 13, background: PS.surface, borderRadius: 18, border: `1px solid ${PS.line}` }}>
            ยังไม่ได้เข้าร่วมวงใด —{' '}
            <span style={{ color: PS.terracotta, fontWeight: 600 }} className="ps-press" onClick={() => nav('/app/discover')}>ค้นหาวงใกล้คุณ</span>
          </div>
        )}
        {items && items.map((it) => (
          <div key={it.circle_id} data-testid={`chat-inbox-${it.circle_id}`} onClick={() => nav(`/app/circle/${it.circle_id}/chat`)}
            className="ps-press" style={{ margin: '0 12px 4px', padding: '12px 12px', display: 'flex', gap: 12, alignItems: 'center', borderRadius: 16, cursor: 'pointer' }}>
            <Avatar name={it.host_name} size={46} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</div>
                {it.verified && <VerifiedMark size={12} />}
                {it.is_demo && <DemoBadge />}
              </div>
              <div style={{ fontSize: 12, color: PS.textMuted, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {it.last_body ? <><span style={{ fontWeight: 500 }}>{it.last_name}:</span> {it.last_body}</> : <span style={{ fontStyle: 'italic', color: PS.textDim }}>ยังไม่มีข้อความ — เริ่มแชทได้เลย</span>}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              {it.last_time && <div style={{ fontSize: 10.5, color: PS.textDim }}>{it.last_time}</div>}
              <Icon.chevronR size={16} stroke={PS.textDim} />
            </div>
          </div>
        ))}
      </div>
    </MemberLayout>
  );
}

// ── Conversation for a single circle ─────────────────────────────────────
function ChatRoom({ circleId }) {
  const nav = useNavigate();
  const [circle, setCircle] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [denied, setDenied] = useState(false);
  const logRef = useRef(null);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const [c, ch] = await Promise.all([api.get(`/circles/${circleId}`), api.get(`/circles/${circleId}/chat`)]);
        if (!on) return;
        setCircle(c.data); setMsgs(ch.data);
      } catch (e) {
        if (on && e?.response?.status === 403) setDenied(true);
      }
    })();
    return () => { on = false; };
  }, [circleId]);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    const body = text.trim(); setText('');
    try {
      const res = await api.post('/chat', { circle_id: circleId, body });
      setMsgs(prev => [...prev, res.data]);
    } catch (e) {
      setText(body);  // restore so the message isn't lost
      notifyError(e?.response?.data?.detail || 'ส่งข้อความไม่สำเร็จ ลองอีกครั้ง');
    }
  };

  if (denied) {
    return (
      <Screen bg={PS.page}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center' }}>
          <Icon.lock size={36} stroke={PS.textMuted} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>เฉพาะสมาชิกในวงเท่านั้น</div>
          <div style={{ fontSize: 13, color: PS.textMuted, lineHeight: 1.5 }}>คุณยังไม่ได้เป็นสมาชิกวงนี้ จึงเข้าแชทไม่ได้ — ขอเข้าร่วมวงก่อนเพื่อพูดคุยกับเพื่อนร่วมวง</div>
          <button data-testid="chat-denied-back" onClick={() => nav('/app/chat')} className="ps-press" style={{ marginTop: 6, background: PS.ink, color: PS.page, border: 'none', borderRadius: 999, padding: '11px 22px', fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>กลับไปหน้าแชท</button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen bg={PS.page}>
      <div style={{ paddingTop: 14, background: PS.surface2, borderBottom: `1px solid ${PS.line}`, flexShrink: 0 }}>
        <div style={{ padding: '6px 16px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button data-testid="chat-back-btn" onClick={() => nav('/app/chat')} className="ps-press" style={{ border: 'none', background: 'transparent', padding: 0 }}>
            <Icon.chevronL size={20} stroke={PS.ink} />
          </button>
          <Avatar name={circle?.host_name || 'แชร์'} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{circle?.name || 'แชทกลุ่ม'}</div>
              {circle?.verified && <VerifiedMark size={12} />}
            </div>
            <div style={{ fontSize: 11, color: PS.textMuted, marginTop: 1, display: 'flex', gap: 5, alignItems: 'center' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: PS.ok }} />{circle?.members || 0} สมาชิก
            </div>
          </div>
          <Icon.info size={20} stroke={PS.ink} />
        </div>
      </div>

      <div ref={logRef} style={{ flex: 1, overflow: 'auto', padding: '12px 16px 16px' }}>
        <div style={{ textAlign: 'center', fontSize: 10, color: PS.textMuted, letterSpacing: 0.5, margin: '4px 0 14px' }}>กลุ่มแชทถูกบันทึกเป็นหลักฐาน · ทุกข้อความมี timestamp</div>
        {msgs.map((m, i) => {
          const showName = !m.mine && (i === 0 || msgs[i - 1].name !== m.name);
          return (
            <div key={m.msg_id || i} className="ps-fade" style={{ display: 'flex', flexDirection: 'column', alignItems: m.mine ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
              {showName && <div style={{ fontSize: 10.5, color: PS.textMuted, padding: '0 12px 3px' }}>{m.name}</div>}
              <div style={{ maxWidth: '78%', padding: '9px 13px', borderRadius: 18, background: m.mine ? PS.ink : PS.surface2, color: m.mine ? PS.page : PS.text, border: m.mine ? 'none' : `1px solid ${PS.line}`, fontSize: 13, lineHeight: 1.4, borderBottomRightRadius: m.mine ? 6 : 18, borderBottomLeftRadius: m.mine ? 18 : 6 }}>{m.body}</div>
              <div style={{ fontSize: 9.5, color: PS.textDim, marginTop: 2, padding: '0 12px' }}>{m.display_time}{m.mine && ' · บันทึกแล้ว'}</div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: '10px 12px', background: PS.surface2, borderTop: `1px solid ${PS.line}`, display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        <div style={{ flex: 1, height: 40, borderRadius: 20, background: PS.panel, display: 'flex', alignItems: 'center', padding: '0 14px' }}>
          <input data-testid="chat-input" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="พิมพ์ข้อความ…" style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, outline: 'none', fontFamily: PS.fontUI, color: PS.text }} />
        </div>
        <button data-testid="chat-send-btn" onClick={send} className="ps-press" style={{ width: 40, height: 40, borderRadius: 20, background: PS.ink, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.send size={16} stroke={PS.page} />
        </button>
      </div>
    </Screen>
  );
}

export default function Chat() {
  const { id: paramId } = useParams();
  return paramId ? <ChatRoom circleId={paramId} /> : <ChatInbox />;
}
